import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Navigation from '../components/Navigation';
import Contact from '../components/Contact';
import SEOComponent from '../components/SEOComponent';
import ImageCarousel from '../components/ImageCarousel';
import { caseStudiesData } from '../data/caseStudiesData';

const CaseStudies = () => {
  const location = useLocation();

  useEffect(() => {
    // Jump directly to the position instantly
    if (location.state?.studyId) {
      const element = document.getElementById(`case-study-${location.state.studyId}`);
      if (element) {
        window.scrollTo({
          top: element.offsetTop - 100,
          behavior: 'instant'
        });
      }
    } else {
      window.scrollTo(0, 0);
    }
  }, [location]);

  return (
    <div className="min-h-screen">
      <Navigation />
      <SEOComponent
          title="Case Studies - Rebel Heat"
          description="Real heat pump readiness and sizing case studies from UK homes and heritage buildings — including an Oxfordshire detached home and a 200-year-old Hampshire church heated for GBP724 a winter. Data-driven heat loss surveys from measured performance."
          url="https://www.rebelheat.co.uk/case-studies"
          image="https://www.rebelheat.co.uk/church_running_costs.jpg"
          keywords="heat pump case studies UK, heat pump sizing example, heat loss survey case study, residential heat pump readiness, heat pump running costs, air source heat pump home, heritage building heat pump, heat pumps for churches, heat pump Hampshire, heat pump Oxfordshire"
          type="article"
          schema={{
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            "name": "Heat Pump Readiness & Sizing Case Studies",
            "description": "Real-world case studies of heat pump readiness assessments and sizing for UK homes, heritage buildings and churches",
            "url": "https://www.rebelheat.co.uk/case-studies",
            "about": {
              "@type": "Thing",
              "name": "Heat Pump Readiness and Sizing"
            }
          }}
        />

      {/* Hero Section */}
      <section className="min-h-screen flex items-center section-padding pt-32">
        <div className="container-max">
          <div className="text-center max-w-4xl mx-auto mb-20">
            <p className="text-xs font-bold tracking-widest text-rebel-coral uppercase mb-4" style={{maxWidth: 'none'}}>
              CASE STUDIES
            </p>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-8 text-rebel-dark">
              Early Results
            </h1>
            <p className="text-xl sm:text-2xl mb-8 text-gray-600 font-light max-w-3xl mx-auto leading-relaxed">
              Real-world deployments delivering measurable insights. These early case studies demonstrate
              the power of data-driven heat loss analysis.
            </p>
            <p className="text-gray-400 italic" style={{maxWidth: 'none'}}>
              Early stage deployments - more comprehensive case studies coming as we scale
            </p>
          </div>
        </div>
      </section>

      {/* Case Studies */}
      {caseStudiesData.map((study, index) => (
        <section
          key={study.id}
          id={`case-study-${study.id}`}
          className="section-padding"
          style={{ backgroundColor: index % 2 === 0 ? '#ffffff' : 'rgb(249, 250, 251)' }}
        >
          <div className="container-max">
            <div className="mb-12">
              <span
                className="inline-block px-4 py-2 rounded-full text-sm font-semibold mb-4"
                style={{
                  backgroundColor: study.category === 'Residential' ? 'rgba(255, 107, 107, 0.2)' : 'rgba(78, 205, 196, 0.2)',
                  color: study.category === 'Residential' ? '#FF6B6B' : '#4ECDC4'
                }}
              >
                {study.category}
              </span>
              <h2 className="text-4xl sm:text-5xl font-bold mb-4 text-rebel-dark">
                {study.title}
              </h2>
              <p className="text-lg text-gray-600">
                {study.location} • {study.buildingType} • {study.size}
              </p>
            </div>

            {/* Images Carousel */}
            <ImageCarousel images={study.images} title={study.title} />

            {/* Challenge & Solution */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
              <div className="game-card p-8">
                <h4 className="text-2xl font-bold text-rebel-dark mb-4">Challenge</h4>
                <p className="text-gray-600 leading-relaxed">{study.challenge}</p>
              </div>
              <div className="game-card p-8">
                <h4 className="text-2xl font-bold text-rebel-dark mb-4">Solution</h4>
                <p className="text-gray-600 leading-relaxed">{study.solution}</p>
              </div>
            </div>

            {/* Key Metrics */}
            <div className="mb-16">
              <h4 className="text-2xl font-bold text-rebel-dark mb-8">Key Metrics</h4>
              <div className="flex justify-between items-start gap-8 flex-wrap">
                {study.keyMetrics.map((metric, idx) => (
                  <div key={idx} className="text-center flex-1 min-w-[150px]">
                    <div className="text-3xl font-bold text-rebel-coral mb-2">
                      {metric.value}
                    </div>
                    <p className="text-sm text-gray-600">
                      {metric.label}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* Results */}
            <div className="mb-16">
              <h4 className="text-2xl font-bold text-rebel-dark mb-8">Results</h4>
              <div className="game-card p-8">
                <ul className="space-y-4">
                  {study.results.map((result, idx) => (
                    <li key={idx} className="flex items-start space-x-3">
                      <svg className="w-6 h-6 text-rebel-green flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                      </svg>
                      <span className="text-gray-600 leading-relaxed">{result}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Testimonial */}
            {study.testimonial && (
              <div className="max-w-3xl mx-auto">
                <div className="game-card p-12 text-center">
                  <p className="text-2xl italic mb-6 text-rebel-dark leading-relaxed">
                    "{study.testimonial.quote}"
                  </p>
                  <p className="text-gray-600 font-semibold">
                    — {study.testimonial.author}, {study.testimonial.role}
                  </p>
                </div>
              </div>
            )}
          </div>
        </section>
      ))}

      {/* Contact Section */}
      <Contact />
    </div>
  );
};

export default CaseStudies;
